import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageMeta from "@/components/feature/PageMeta";
import PosterCard from "@/components/feature/PosterCard";
import SectionHeader from "@/components/feature/SectionHeader";
import DemoPlayer from "@/pages/detail/components/DemoPlayer";
import { getRelated, getTitleById, typeLabels } from "@/lib/catalog";
import { useFavorites } from "@/lib/favorites";

export default function DetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const item = getTitleById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "auto" });
  }, [id]);

  useEffect(() => {
    if (!toast) return;
    const timer = window.setTimeout(() => setToast(""), 2400);
    return () => window.clearTimeout(timer);
  }, [toast]);

  if (!item) {
    return (
      <>
        <PageMeta
          title="影视信息不存在或已下架 -日韩在线"
          description="您访问的影视信息不存在或已下架，请返回首页浏览最新日韩电影电视剧。"
          keywords="日韩在线,日韩影视,在线观看"
        />
        <SiteLayout topOffset>
          <div className="w-full px-4 md:px-8 py-24 flex flex-col items-center text-center">
            <span className="w-16 h-16 rounded-full bg-background-100 text-foreground-400 flex items-center justify-center">
              <i className="ri-error-warning-line text-3xl w-8 h-8 flex items-center justify-center" />
            </span>
            <h1 className="mt-6 font-heading text-xl md:text-2xl font-black text-foreground-950">
              影视信息不存在或已下架
            </h1>
            <p className="mt-2 text-sm text-foreground-600">该影片可能已调整或链接有误，返回首页看看其他作品吧。</p>
            <div className="mt-6 flex items-center gap-3">
              <Link
                to="/"
                className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md bg-primary-600 text-background-50 text-sm font-label font-semibold whitespace-nowrap hover:bg-primary-700 transition-colors"
              >
                返回首页
              </Link>
              <Link
                to="/category/movie"
                className="inline-flex items-center gap-1.5 h-10 px-5 rounded-md border border-background-300 text-foreground-800 text-sm font-label font-semibold whitespace-nowrap hover:bg-background-100 transition-colors"
              >
                浏览电影
              </Link>
            </div>
          </div>
        </SiteLayout>
      </>
    );
  }

  const favorite = isFavorite(item.id);
  const related = getRelated(item, 4);

  const handleFavorite = () => {
    const next = toggleFavorite(item.id);
    setToast(next ? "已加入收藏，可在「我的收藏」查看" : "已取消收藏");
  };

  const handleShare = async () => {
    const shareUrl = `${window.location.origin}${__BASE_PATH__}/detail/${item.id}`;
    try {
      if (typeof navigator.share === "function") {
        await navigator.share({ title: item.title, text: item.tagline, url: shareUrl });
        setToast("已调起系统分享");
        return;
      }
      await navigator.clipboard.writeText(shareUrl);
      setToast("链接已复制到剪贴板");
    } catch {
      setToast("分享已取消");
    }
  };

  return (
    <>
      <PageMeta
        title={`${item.title} - 在线观看 -日韩在线`}
        description={`${item.synopsis.slice(0, 110)}…`}
        keywords={`${item.title},${item.originalTitle},${item.genre.join(",")},在线观看日韩影视,${item.region}影视`}
      />
      <SiteLayout topOffset>
        {/* Backdrop hero */}
        <section className="relative w-full h-[260px] md:h-[340px] overflow-hidden bg-foreground-950">
          <img
            src={item.backdrop}
            alt={`${item.title} 剧照`}
            title={`${item.title} 在线观看日韩影视`}
            className="w-full h-full object-cover opacity-70"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-foreground-950/50 to-foreground-950/40" />
          <div className="absolute top-4 left-4 md:left-8">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="inline-flex items-center gap-1.5 h-8 px-3 rounded-md bg-background-50/15 backdrop-blur-sm text-[11px] font-label font-semibold text-background-50 hover:bg-background-50/25 transition-colors cursor-pointer whitespace-nowrap"
            >
              <i className="ri-arrow-left-line w-3.5 h-3.5 flex items-center justify-center" />
              返回上一页
            </button>
          </div>
        </section>

        <section className="w-full px-4 md:px-8 -mt-24 md:-mt-32 relative z-10 pb-12 md:pb-16">
          <nav className="flex items-center gap-1.5 text-xs text-foreground-500 flex-wrap mb-4" aria-label="面包屑">
            <Link to="/" className="hover:text-foreground-900 transition-colors">首页</Link>
            <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center" />
            <Link
              to={`/category/${item.type}`}
              className="hover:text-foreground-900 transition-colors"
            >
              {typeLabels[item.type]}
            </Link>
            <i className="ri-arrow-right-s-line w-3.5 h-3.5 flex items-center justify-center" />
            <span className="text-foreground-800">{item.title}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10">
            {/* Left column */}
            <div className="lg:col-span-5">
              <div className="rounded-lg overflow-hidden border border-background-200 bg-background-50 w-full max-w-[320px] mx-auto lg:max-w-none">
                <div className="w-full aspect-[2/3] bg-background-200">
                  <img
                    src={item.poster}
                    alt={`${item.title} 海报`}
                    title={`${item.title} 在线观看日韩影视`}
                    className="w-full h-full object-cover object-top"
                  />
                </div>
              </div>

              <div className="mt-4 grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={handleFavorite}
                  className={`inline-flex items-center justify-center gap-1.5 h-11 rounded-md text-sm font-label font-semibold whitespace-nowrap cursor-pointer transition-colors ${
                    favorite
                      ? "bg-accent-500 text-background-50 hover:bg-accent-600"
                      : "bg-primary-600 text-background-50 hover:bg-primary-700"
                  }`}
                >
                  <i className={`${favorite ? "ri-heart-fill" : "ri-heart-line"} w-4 h-4 flex items-center justify-center text-base`} />
                  {favorite ? "已收藏" : "收藏影视"}
                </button>
                <button
                  type="button"
                  onClick={handleShare}
                  className="inline-flex items-center justify-center gap-1.5 h-11 rounded-md bg-background-100 border border-background-200 text-foreground-800 text-sm font-label font-semibold whitespace-nowrap hover:bg-background-200 transition-colors cursor-pointer"
                >
                  <i className="ri-share-forward-line w-4 h-4 flex items-center justify-center text-base" />
                  分享链接
                </button>
              </div>

              <div className="mt-4 rounded-lg bg-background-100 border border-background-200/70 p-4">
                <h3 className="font-label text-[11px] font-semibold tracking-[0.18em] uppercase text-foreground-500">
                  基本信息
                </h3>
                <dl className="mt-3 space-y-2.5 text-sm">
                  {[
                    { label: "类型", value: typeLabels[item.type] },
                    { label: "上映年份", value: String(item.year) },
                    { label: "地区", value: item.region },
                    { label: "题材", value: item.genre.join(" / ") },
                    { label: "导演", value: item.director },
                    { label: "集数 / 片长", value: item.episodes },
                  ].map((row) => (
                    <div key={row.label} className="flex items-start gap-3">
                      <dt className="shrink-0 w-20 text-xs text-foreground-500 pt-0.5">{row.label}</dt>
                      <dd className="text-[13px] text-foreground-900 leading-snug">{row.value}</dd>
                    </div>
                  ))}
                  <div className="flex items-start gap-3">
                    <dt className="shrink-0 w-20 text-xs text-foreground-500 pt-0.5">主演</dt>
                    <dd className="flex flex-wrap gap-1.5">
                      {item.cast.map((name) => (
                        <Link
                          key={name}
                          to={`/search?q=${encodeURIComponent(name)}`}
                          className="px-2 py-0.5 rounded-md bg-background-50 border border-background-200 text-[11px] text-foreground-700 hover:border-primary-300 hover:text-primary-700 transition-colors"
                        >
                          {name}
                        </Link>
                      ))}
                    </dd>
                  </div>
                </dl>
              </div>
            </div>

            {/* Right column */}
            <div className="lg:col-span-7">
              <div className="flex flex-wrap items-center gap-2">
                <span className="px-2.5 py-1 rounded-md bg-primary-600 text-background-50 text-[11px] font-label font-bold">
                  {typeLabels[item.type]}
                </span>
                <span className="px-2.5 py-1 rounded-md bg-secondary-100 text-secondary-900 text-[11px] font-label font-semibold">
                  {item.region}
                </span>
                <span className="px-2.5 py-1 rounded-md bg-background-100 border border-background-200 text-foreground-700 text-[11px] font-label font-semibold">
                  {item.year}
                </span>
                {item.isNew && (
                  <span className="px-2.5 py-1 rounded-md bg-accent-100 text-accent-800 text-[11px] font-label font-bold">
                    最新上线
                  </span>
                )}
              </div>

              <h1 className="mt-3 font-heading text-2xl md:text-4xl font-black text-foreground-950 leading-tight tracking-tight">
                {item.title}
              </h1>
              <p className="mt-1.5 font-label text-xs md:text-sm tracking-[0.12em] uppercase text-foreground-400">
                {item.originalTitle}
              </p>

              <div className="mt-4 flex items-center gap-4 flex-wrap">
                <div className="flex items-center gap-1.5">
                  <i className="ri-star-fill text-accent-500 w-4 h-4 flex items-center justify-center" />
                  <span className="font-label text-xl font-black text-foreground-950">{item.rating.toFixed(1)}</span>
                  <span className="text-xs text-foreground-400 font-label">/ 10</span>
                </div>
                <span className="w-px h-5 bg-background-200" />
                <span className="text-xs text-foreground-600">站内热度 {item.heat}</span>
                <span className="w-px h-5 bg-background-200" />
                <span className="text-xs text-foreground-600">{item.episodes}</span>
              </div>

              <p className="mt-4 text-sm md:text-[15px] text-accent-800 bg-accent-50 border-l-2 border-accent-400 pl-3 py-1.5 leading-relaxed">
                {item.tagline}
              </p>

              <div className="mt-6">
                <DemoPlayer poster={item.backdrop} title={item.title} episodes={item.episodes} />
              </div>

              <div className="mt-6">
                <h2 className="font-heading text-base md:text-lg font-black text-foreground-950">剧情简介</h2>
                <p className="mt-2.5 text-sm text-foreground-700 leading-[1.9]">{item.synopsis}</p>
              </div>

              <div className="mt-6 rounded-lg bg-background-100 border border-background-200/70 p-4 flex items-start gap-3">
                <span className="shrink-0 w-8 h-8 rounded-md bg-background-50 border border-background-200 text-foreground-500 flex items-center justify-center">
                  <i className="ri-shield-check-line w-4 h-4 flex items-center justify-center" />
                </span>
                <p className="text-xs text-foreground-600 leading-relaxed">
                  温馨提示：本页播放区域仅为前端演示，点击播放会提示「本功能为展示示范，实际播放待接入」。
                  站点不托管任何影视资源，也不提供下载。
                </p>
              </div>

              <div className="mt-6 flex items-center gap-2 flex-wrap">
                <Link
                  to={`/category/${item.type}`}
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-background-100 border border-background-200 text-xs font-label font-semibold text-foreground-800 whitespace-nowrap hover:bg-background-200 transition-colors"
                >
                  <i className="ri-arrow-left-line w-3.5 h-3.5 flex items-center justify-center" />
                  返回{typeLabels[item.type]}分类
                </Link>
                <Link
                  to="/"
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-background-100 border border-background-200 text-xs font-label font-semibold text-foreground-800 whitespace-nowrap hover:bg-background-200 transition-colors"
                >
                  <i className="ri-home-4-line w-3.5 h-3.5 flex items-center justify-center" />
                  回到首页
                </Link>
                <Link
                  to={`/search?q=${encodeURIComponent(item.region)}`}
                  className="inline-flex items-center gap-1.5 h-9 px-4 rounded-md bg-background-100 border border-background-200 text-xs font-label font-semibold text-foreground-800 whitespace-nowrap hover:bg-background-200 transition-colors"
                >
                  <i className="ri-search-line w-3.5 h-3.5 flex items-center justify-center" />
                  更多{item.region}影视
                </Link>
              </div>
            </div>
          </div>
        </section>

        {related.length > 0 && (
          <section className="w-full px-4 md:px-8 pb-14 md:pb-20">
            <SectionHeader
              eyebrow="You May Also Like"
              title="相关推荐"
              description={`根据题材与地区为你挑选的${typeLabels[item.type]}，风格相近，值得一看。`}
              action={{ label: `更多${typeLabels[item.type]}`, to: `/category/${item.type}` }}
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-x-3 gap-y-7 md:gap-x-5 md:gap-y-9">
              {related.map((rel) => (
                <PosterCard key={rel.id} item={rel} />
              ))}
            </div>
          </section>
        )}

        {toast && (
          <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[70] animate-fade-up">
            <div className="flex items-center gap-2 px-4 py-2.5 rounded-md bg-foreground-950 text-background-50 text-xs font-label whitespace-nowrap">
              <i className="ri-checkbox-circle-line text-accent-400 w-4 h-4 flex items-center justify-center" />
              {toast}
            </div>
          </div>
        )}
      </SiteLayout>
    </>
  );
}